import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Autocomplete, Checkbox, Chip, TextField } from '@mui/material';
import * as _ from 'lodash';
import type React from 'react';
import { useEffect, useState } from 'react';

import itemUtils from '@/utils/itemUtils';

interface IProps {
  id: string;
  items: Array<any>;
  itemKey: string;
  itemText: string;
  itemTagText?: string;
  labelText: string;
  multiSelect: boolean;
  placeHolderText: string;
  controlWidth?: number;
  labelPaddingTop?: string;
  defaultValue?: any;
  onBlur?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onChange: (value: any) => void;
  isRequired?: boolean;
  hasError?: boolean;
  disabled?: boolean;
  allowEmptyValue?: boolean;
  groupBy?: string;
  allowSelectAllOption?: boolean;
  limitTags?: number;
  noOptionsText?: string;
  helperText?: string;
}
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const FuelAutoComplete = ({
  id,
  items,
  itemKey,
  itemText,
  itemTagText,
  labelText,
  multiSelect,
  placeHolderText,
  controlWidth,
  labelPaddingTop,
  defaultValue,
  onChange,
  onBlur,
  isRequired = false,
  hasError = false,
  disabled = false,
  allowEmptyValue = false,
  groupBy = null,
  allowSelectAllOption = false,
  limitTags = 1,
  noOptionsText = null,
  helperText = null,
}: IProps) => {
  // Set defaults
  if (placeHolderText == null) placeHolderText = 'Select option';
  if (!itemKey) itemKey = 'id';
  if (!itemTagText) itemTagText = itemText;
  if (!items) items = [];

  // console.log('FuelSelect', { defaultValue, items, multiSelect });

  //   const [options, setOptions] = useState([]);
  const [selectedValues, setSelectedValues] = useState(multiSelect ? [] : '');

  const allSelected = multiSelect ? selectedValues.length === items.length : false;

  const handleChange = (event, values) => {
    if (multiSelect) {
      values = itemUtils.ensureArray(values);
      const selectAllOption = values.find((option) => option[id] === 'selectAll');
      if (selectAllOption) {
        handleSelectAllToggle();
      } else {
        setSelectedValues(values);
        onChange(values);
      }
    } else {
      setSelectedValues(values);
      onChange(values);
    }
  };

  const handleSelectAllToggle = () => {
    if (allSelected) {
      setSelectedValues([]);
      onChange([]);
    } else {
      setSelectedValues(items);
      onChange(items);
    }
  };

  const removeOption = (id) => { };

  const getOptionLabel = (option) => {
    if (option && option[itemText]) {
      return option[itemText].toString();
    }
    return '';
  };

  useEffect(() => {
    // console.log('multiSelect, defaultValue changed', { multiSelect, defaultValue });
    if (!defaultValue) {
      if (multiSelect) {
        defaultValue = [];
      } else {
        defaultValue = '';
      }
    } else if (multiSelect) {
      defaultValue = itemUtils.ensureArray(defaultValue);
    }
    setSelectedValues(defaultValue);
  }, [multiSelect, defaultValue]);

  return (
    <Autocomplete
      noOptionsText={noOptionsText || 'No options'}
      className="fuel-autocomplete"
      multiple={multiSelect}
      options={allowSelectAllOption && multiSelect ? [{ [itemText]: 'Select All', [id]: 'selectAll' }, ...items] : items}
      id={id}
      disableCloseOnSelect={multiSelect || false}
      getOptionLabel={getOptionLabel}
      value={selectedValues}
      limitTags={limitTags}
      disabled={disabled}
      disableClearable={!allowEmptyValue}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            title={option[itemText]}
            variant="outlined"
            label={option[itemTagText]}
            size="small"
            {...getTagProps({ index })}
            key={index}
          />
        ))
      }
      // renderTags={(tagValue, getTagProps) =>
      //   tagValue.map((option, index) => (
      //     <Chip id={`option-${index}-${tagValue}`} title={option.title} label={option.title} {...getTagProps({ index })} />
      //   ))
      // }
      // getOptionSelected={(option, value) => option.id === value.id}
      renderOption={(props, option, { selected }) => {
        if (multiSelect) {
          const isSelectAll = option[id] === 'selectAll';
          return (
            <li {...props} key={option[itemKey]}>
              <Checkbox
                size="small"
                color="success"
                style={{
                  transform: 'scale(.6)',
                  marginRight: 8,
                }}
                checked={isSelectAll ? allSelected : selected}
              />
              {option[itemText]}
            </li>
          );
        }
        return (
          <li {...props} key={option[itemKey]}>
            {option[itemText]}
          </li>
        );
      }}
      onChange={handleChange}
      onBlur={(e) => onBlur && onBlur(e)}
      groupBy={groupBy}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={labelText}
          placeholder={placeHolderText}
          required={isRequired}
          error={hasError}
          inputProps={{
            ...params.inputProps,
            autoComplete: 'off',
          }}
          helperText={helperText}
        />
      )}
      isOptionEqualToValue={(option, value) => {
        // No selection -> no option should be considered selected
        if (value === undefined || value === null || value === '') {
          return false;
        }
        if (!multiSelect && _.isArray(value)) {
          if (value.length === 0) {
            return false;
          }
          return option[itemKey] === value[0][itemKey];
        }
        return option[itemKey] === value[itemKey];
      }}
    />
  );
};

export default FuelAutoComplete;
